import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import BOLayout from '@/pages/back-office/BOLayout';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const NotificationForm = () => {
    const { notificationId } = useParams();
    const [notification, setNotification] = useState({
        name: '',
        message: '',
        type: '',
        startDate: '',
        endDate: '',
        global: false,
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (notificationId) {
            const fetchNotification = async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/notifications/${notificationId}`);
                    if (response.status === 200) {
                        setNotification(response.data);
                    }
                } catch (error) {
                    toast.error('Erreur lors du chargement de la notification', {
                        position: 'top-center',
                        autoClose: 1000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: false,
                        draggable: true,
                        theme: 'colored',
                    });
                } finally {
                    setLoading(false);
                }
            };
            fetchNotification();
        } else {
            setLoading(false);
        }
    }, [notificationId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (notificationId) {
                await axios.put(`${import.meta.env.VITE_API_URL}/api/notifications/${notificationId}`, notification);
                toast.success('Notification modifiée avec succès');
            } else {                
                await axios.post(`${import.meta.env.VITE_API_URL}/api/notifications`, notification);
                toast.success('Notification créée avec succès');
            }
            navigate(`/back-office/notifications/list`);
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement de la notification');
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) {
            return '';  // Si la date est undefined ou null, retourner une chaîne vide
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return '';  // Si la date n'est pas valide, retourner une chaîne vide
        }
        return date.toISOString().split('T')[0];  // Formater en 'YYYY-MM-DD'
    };

    const handleQuillChange = (value) => {
        setNotification({ ...notification, message: value });
    };

    if (loading) {
        return <div>Chargement...</div>;
    }

    return (
        <BOLayout>
            <form onSubmit={handleSubmit} className="w-full max-w-lg mx-auto p-4">
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">Nom de la notification</label>
                    <input
                        type="text"
                        name="name"
                        value={notification.name || ''}
                        onChange={(e) => setNotification({ ...notification, name: e.target.value })}
                        className="border p-2 w-full"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="message">Message</label>
                    <ReactQuill
                        theme="snow"
                        value={notification.message}
                        onChange={handleQuillChange}
                        modules={NotificationForm.modules}
                        formats={NotificationForm.formats}
                        bounds={'.app'}
                        placeholder="Entrez un message..."
                    />
                </div>

                {/* Sélecteur de type de notification */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="type">Type de notification</label>
                    <select
                        name="type"
                        value={notification.type}
                        onChange={(e) => setNotification({ ...notification, type: e.target.value })}
                        className="border p-2 w-full"
                        required
                    >
                        <option value="">Sélectionner un type</option>
                        <option value="push">Push</option>
                        <option value="popup">Popup</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="startDate">Date de début</label>
                    <input
                        type="date"
                        name="startDate"
                        value={formatDateForInput(notification.startDate) || ''}
                        onChange={(e) => setNotification({ ...notification, startDate: e.target.value })}
                        className="border p-2 w-full"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">Date de fin (facultatif)</label>
                    <input
                        type="date"
                        name="endDate"
                        value={formatDateForInput(notification.endDate) || ''}
                        onChange={(e) => setNotification({ ...notification, endDate: e.target.value })}
                        className="border p-2 w-full"
                    />
                </div>

                {/* Nouveau champ pour définir si la notification est pour tous les utilisateurs */}
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                        Envoyer à tous les utilisateurs
                    </label>
                    <input
                        type="checkbox"
                        checked={notification.global}
                        onChange={(e) => setNotification({ ...notification, global: e.target.checked })}
                    />
                </div>

                <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                    {notificationId ? 'Modifier la notification' : 'Créer la notification'}
                </button>
            </form>
        </BOLayout>
    );
};

NotificationForm.modules = {
    toolbar: [
        [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
        [{ 'size': [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, 
         {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image', 'video'],
        ['clean']
    ],
    clipboard: {
        // Match visual, pas de style inline
        matchVisual: false,
    }
};

NotificationForm.formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video'
];

export default NotificationForm;
